import { ExportOutlined } from '@ant-design/icons';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useActivityTree } from 'business/shift-report/report-analyze/pages/activity-analyze/components/aside-activity-tree/use-activity-tree';
import {
  useReportActivityAnalyzeActivities,
  useReportActivityAnalyzeFilters,
} from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters';
import { useActivityAnalyseLegendFilters } from 'business/shift-report/report-analyze/pages/activity-analyze/providers/legend-filters';
import {
  ACTIVITY_DISPLAY_TYPES_MAP,
  ACTIVITY_GROUP_BY_TYPES_MAP,
} from 'business/shift-report/report-analyze/pages/activity-analyze/types';
import { ActivityMinimalTreeFragment, Module_Enum } from 'generated/graphql';
import { getFetchRange } from 'technical/range-filters/get-fetch-range';
import Button from 'ui/button';

import { useReportAnalyzeAnalyzeExport } from './use-report-activity-analyze-export';

type ActivityTreeTraversal = {
  id: string | null;
  children: ActivityTreeTraversal[] | null;
};

const activityTreeTraversal = (
  activity: ActivityMinimalTreeFragment,
  checkedActivities: string[],
): ActivityTreeTraversal => {
  const selectedChildren = activity.children
    ?.map((childActivity) => {
      return activityTreeTraversal(
        childActivity as ActivityMinimalTreeFragment,
        checkedActivities,
      );
    })
    .filter((childActivity) => {
      return childActivity.id || childActivity.children;
    });

  return {
    id:
      checkedActivities.includes(activity.id) || selectedChildren?.length
        ? activity.id
        : null,
    children: selectedChildren?.length ? selectedChildren : null,
  };
};

const createCheckedActivityTree = (
  checkedActivities: ActivityMinimalTreeFragment[],
  checkedIds: string[],
): ActivityTreeTraversal[] => {
  const topLevel = checkedActivities.map((activity) => {
    return activityTreeTraversal(activity, checkedIds);
  });

  return topLevel;
};

const selectedLeavesTraversal = (
  activity: ActivityTreeTraversal,
  selectedActivities: string[],
  getLeaves: boolean,
): string[] => {
  if (!activity.id) {
    return [];
  }

  const foundCurrentActivity = selectedActivities.includes(activity.id);

  if (!activity.children) {
    return getLeaves || foundCurrentActivity ? [activity.id] : [];
  }

  return activity.children.flatMap((childActivity) => {
    const getChildActivityLeaves = getLeaves || foundCurrentActivity;

    return selectedLeavesTraversal(
      childActivity,
      selectedActivities,
      getChildActivityLeaves,
    );
  });
};

const getSelectedLeaves = (
  checkedActivityTree: ActivityTreeTraversal[],
  selectedActivities: string[],
  getLeaves: boolean,
) => {
  const topLevel = checkedActivityTree
    .flatMap((activity) => {
      return selectedLeavesTraversal(activity, selectedActivities, getLeaves);
    })
    .filter(Boolean);

  return topLevel;
};

const ReportActivityAnalyzeExportButton = () => {
  const { t } = useTranslation();
  const { currentConstructionSiteId } = useAppContext();
  const headers = useRestApiHeaders(Module_Enum.ShiftReport);
  invariant(currentConstructionSiteId, 'No constructionSite id');

  const filters = useReportActivityAnalyzeFilters();
  const checkedKeys = useReportActivityAnalyzeActivities();

  const { generateExport, isPending } = useReportAnalyzeAnalyzeExport();
  const fetchRange = getFetchRange(filters);

  const activityTreeMetadata = useActivityTree({
    constructionSiteId: currentConstructionSiteId,
  });

  const { legendFilters } = useActivityAnalyseLegendFilters();

  const checkedActivityTree = createCheckedActivityTree(
    activityTreeMetadata.displayedActivities as ActivityMinimalTreeFragment[],
    checkedKeys,
  );

  const selectedActivities = getSelectedLeaves(
    checkedActivityTree,
    legendFilters.selectedActivities,
    legendFilters.allActivities,
  );

  const onClick = () => {
    generateExport({
      body: {
        constructionSiteId: currentConstructionSiteId,
        groupByType:
          filters.displayType === ACTIVITY_DISPLAY_TYPES_MAP.pie
            ? ACTIVITY_GROUP_BY_TYPES_MAP.activity
            : filters.groupByType,
        aggregationLevel: filters.aggregationLevel,
        criticalType: filters.criticalType,
        selectedActivityId: legendFilters.allActivities
          ? checkedKeys
          : selectedActivities,
        locale: getLocale(),
        ...fetchRange,
      },
      headers,
    });
  };

  return (
    <Button icon={<ExportOutlined />} onClick={onClick} loading={isPending}>
      {t('common.export')}
    </Button>
  );
};

export default ReportActivityAnalyzeExportButton;
